/**
 * ClinicalToolsSubmenu Component (EVI-721)
 * Restyled integration partners pop-out with search input and checkboxes.
 * Uses existing atoms directly — no separate logic stack.
 *
 * EVI-1093: HealthPathways partners with needsSetup open a setup dialog
 * instead of toggling.
 */

import { healthPathwaysSetupDialogOpenAtom } from '@/atoms/healthPathwaysAtoms';
import { PartnerLogo } from '@heidi/evidence';
import {
  addingMoreJournalsDailyMessage,
  fullTextSearchComingSoonMessage,
  hasWebSearchJournals,
  type IntegrationPartnerConfig,
} from '@/composition/hosts/evidence/integrationPartners';
import { useAvailableIntegrations } from '@/components/chat/hooks/useAvailableIntegrations';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import { Button } from '@heidi/design-system/button';
import { Checkbox } from '@heidi/design-system/checkbox';
import { SearchInput } from '@heidi/design-system/search-input';
import { PopoverMenuItem } from '@heidi/design-system/popover-menu-item';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@heidi/design-system/popover';
import { TypographyCaption } from '@heidi/design-system/typography';
import { useExpandedSourceControlTrackingEnabled } from '@/features/flags';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { ButtonAction } from '@heidi/runtime/analytics-vocabulary';
import type { TranslationKey } from '@heidi/runtime/i18n';
import { useTranslations } from '@heidi/runtime/i18n';
import { useSetAtom } from 'jotai';
import { BadgeCheck, ChevronRight, Globe, Settings } from 'lucide-react';
import { useRouter } from 'next/router';
import { useCallback, useState, type MouseEvent } from 'react';
import { useIntl } from 'react-intl';
import { SHAREPOINT_SETTINGS_PATH } from '@/container/integrations/sharepoint-management/sharepoint-paths';
import { useSourceControlActions } from '../hooks/useSourceControlActions';
import { track } from '@/lib/analytics/track';

interface ClinicalToolsSubmenuProps {
  disabled: boolean;
  /** Close the parent source-control popover before opening a partner setup dialog. */
  onOpenPartnerSetup?: () => void;
}

/**
 * Partners that finish setup on a dedicated Settings → Integrations page.
 * Keys must match a real static settings route (not marketplace [integrationSlug]).
 */
const PAGE_BASED_SETUP_ROUTES: Partial<
  Record<IntegrationPartnerConfig['id'], string>
> = {
  sharepoint: SHAREPOINT_SETTINGS_PATH,
};

export const ClinicalToolsSubmenu = ({
  disabled,
  onOpenPartnerSetup,
}: ClinicalToolsSubmenuProps) => {
  const { t } = useTranslations();
  const intl = useIntl();
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const openSetupDialog = useSetAtom(healthPathwaysSetupDialogOpenAtom);
  const router = useRouter();
  const { baseProps } = useEvidenceAnalytics();

  const handleSetupClick = useCallback(
    (partner: IntegrationPartnerConfig) => {
      setSubmenuOpen(false);
      // HealthPathways sets up via an inline dialog. Page-based partners
      // (e.g. SharePoint) use an explicit route map — do not derive paths
      // from partner.id alone (marketplace [integrationSlug] is a different
      // data source and will 404/redirect for unknown keys).
      if (partner.id === 'healthpathways') {
        onOpenPartnerSetup?.();
        openSetupDialog(true);
        return;
      }
      const setupPath = PAGE_BASED_SETUP_ROUTES[partner.id];
      if (setupPath) {
        void router.push(setupPath);
      }
    },
    [onOpenPartnerSetup, openSetupDialog, router]
  );

  const handleEditSetup = useCallback(
    (partner: IntegrationPartnerConfig, event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (!partner.canEditSetup) return;
      setSubmenuOpen(false);
      // The HP setup dialog auto-detects edit mode from existing setup_config.
      // Future partners with their own edit dialog atoms should branch here.
      if (partner.id === 'healthpathways') {
        onOpenPartnerSetup?.();
        openSetupDialog(true);
      }
    },
    [onOpenPartnerSetup, openSetupDialog]
  );

  const { toggleState, handlePartnerToggle } = useSourceControlActions(baseProps);
  const { partners: availablePartners } = useAvailableIntegrations();

  const hasAnyPartnerEnabled = availablePartners.some(
    (partner) => toggleState.integrationPartners[partner.id] === true
  );

  const isExpandedSourceTrackingEnabled = useExpandedSourceControlTrackingEnabled();

  const handleSubmenuOpenChange = useCallback(
    (open: boolean) => {
      if (disabled) return;
      setSubmenuOpen(open);
      if (!open) setSearchQuery('');
      if (open && isExpandedSourceTrackingEnabled) {
        track({
          event_name: TrackingEventName.ButtonClicked,
          props: {
            ...baseProps,
            action: ButtonAction.EvidenceSourcesListClicked,
            label: 'Partner sources submenu',
            feature: 'evidence',
          },
        });
      }
    },
    [disabled, isExpandedSourceTrackingEnabled, baseProps]
  );

  const filteredPartners = availablePartners.filter((partner) =>
    partner.displayName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getSummary = () => {
    const enabledPartners = availablePartners.filter(
      (partner) => toggleState.integrationPartners[partner.id] === true
    );
    if (enabledPartners.length === 0) return null;
    if (enabledPartners.length === 1) return enabledPartners[0]?.displayName;
    return `${enabledPartners.length} selected`;
  };

  if (availablePartners.length === 0) return null;

  const summary = getSummary();

  return (
    <Popover open={submenuOpen} onOpenChange={handleSubmenuOpenChange}>
      <PopoverTrigger asChild disabled={disabled}>
        <PopoverMenuItem
          disabled={disabled}
          className="data-[state=open]:bg-surface-hover"
          aria-haspopup="true"
          aria-expanded={submenuOpen}
          aria-label={t('sources.sourceControl.clinicalTools' as TranslationKey)}
          icon={<BadgeCheck className="size-4" />}
          accessoryEnd={
            <span className="flex items-center gap-1">
              {/* The accessory never shrinks, so the cap is what leaves the
                  label room; `sm:` tracks the popover's `sm:min-w-96`. */}
              {!disabled && hasAnyPartnerEnabled && (
                <TypographyCaption
                  className="max-w-20 truncate text-text-tertiary sm:max-w-40"
                  title={summary ?? undefined}
                >
                  {summary}
                </TypographyCaption>
              )}
              <ChevronRight className="size-4 text-text-secondary rtl:-scale-x-100" />
            </span>
          }
        >
          {t('sources.sourceControl.clinicalTools' as TranslationKey)}
        </PopoverMenuItem>
      </PopoverTrigger>

      <PopoverContent
        side="right"
        align="end"
        sideOffset={4}
        collisionPadding={16}
        className="w-64 p-1"
      >
        <div className="flex flex-col">
          {/* Search input */}
          <div className="p-1">
            <SearchInput
              placeholder={t('sources.sourceControl.clinicalToolsSearchPlaceholder' as TranslationKey)}
              clearAriaLabel={intl.formatMessage({
                defaultMessage: 'Clear search',
                id: '4YJHutR26p',
              })}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Partner list */}
          <div className="h-[180px] overflow-y-auto">
          {filteredPartners.map((partner) => {
            const isEnabled = toggleState.integrationPartners[partner.id] ?? false;

            if (partner.needsSetup) {
              return (
                <PopoverMenuItem
                  key={partner.id}
                  // Single-line partner rows: center the logo against the label.
                  // PopoverMenuItem top-aligns by default for description (multi-line) items.
                  className="items-center"
                  onClick={() => handleSetupClick(partner)}
                  icon={
                    <PartnerLogo
                      src={partner.logo}
                      brandColor={partner.brandColor}
                      size={16}
                      alt={partner.displayName}
                    />
                  }
                  accessoryEnd={
                    <TypographyCaption className="text-text-link">
                      {t('integrationPartners.healthpathways.setUp' as TranslationKey)}
                    </TypographyCaption>
                  }
                >
                  {partner.displayName}
                </PopoverMenuItem>
              );
            }

            return (
              <PopoverMenuItem
                key={partner.id}
                // Single-line partner rows: center the logo against the label.
                className="items-center"
                onClick={() => handlePartnerToggle(partner.id, { displayName: partner.displayName })}
                icon={
                  <PartnerLogo
                    src={partner.logo}
                    brandColor={partner.brandColor}
                    size={16}
                    alt={partner.displayName}
                  />
                }
                accessoryEnd={
                  <span className="flex items-center gap-1">
                    {partner.canEditSetup && (
                      <Button
                        variant="ghost"
                        size="xs"
                        iconOnly
                        onClick={(event) => handleEditSetup(partner, event)}
                        aria-label={intl.formatMessage(
                          {
                            defaultMessage: 'Edit {partner} setup',
                            id: 'sources.sourceControl.editPartnerSetup',
                          },
                          { partner: partner.displayName }
                        )}
                        data-testid={`edit-partner-setup-${partner.id}`}
                      >
                        <Settings />
                      </Button>
                    )}
                    <Checkbox
                      checked={isEnabled}
                      onCheckedChange={() => handlePartnerToggle(partner.id, { displayName: partner.displayName })}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </span>
                }
                selected={isEnabled}
                description={
                  partner.useTavily
                    ? intl.formatMessage(fullTextSearchComingSoonMessage)
                    : undefined
                }
              >
                {partner.useTavily ? (
                  <span className="inline-flex items-center gap-1">
                    {partner.displayName}
                    <Globe
                      className="size-3.5 shrink-0 text-text-tertiary"
                      aria-hidden
                      data-testid={`partner-web-search-${partner.id}`}
                    />
                  </span>
                ) : (
                  partner.displayName
                )}
              </PopoverMenuItem>
            );
          })}

          {filteredPartners.length === 0 && searchQuery && (
            <div className="p-2">
              <TypographyCaption className="text-text-tertiary">
                {t('sources.sourceControl.noResults' as TranslationKey)}
              </TypographyCaption>
            </div>
          )}
          </div>
          {hasWebSearchJournals(availablePartners) && (
            <TypographyCaption className="px-2 py-1 text-text-tertiary">
              {intl.formatMessage(addingMoreJournalsDailyMessage)}
            </TypographyCaption>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
